import { useRouter } from 'expo-router'
import { useCallback, useEffect } from 'react'

import type { TMe } from '@/src/features/auth/models/auth.types'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import type { TCommunityJoinParams } from '@/src/general/constants/routes'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useRoutes } from '@/src/general/utils/routes'

import type { TMediumCommunityApi } from '../../api/communityApi.types'
import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '../../models/community.constants'
import { useCommunityQuery } from '../../services/useCommunityQuery'

type TJoinStep = 'date' | 'push' | 'email' | 'other'

const buildJoinSteps = (community?: Pick<TMediumCommunityApi, 'activity' | 'lifetime'>, me?: TMe) => {
  const steps: TJoinStep[] = []
  if (
    community?.activity !== COMMUNITY_ACTIVITY.COMING_SOON &&
    community?.lifetime !== COMMUNITY_LIFETIME.EVERLASTING
  ) {
    steps.push('date')
  }
  steps.push('push')
  if (me?.behavioralSegment === 'core-active' || me?.behavioralSegment === 'site-consultation') {
    steps.push('email')
  }
  return steps
}

const getNextJoinRouteName = (stepName: TJoinStep, steps: TJoinStep[]) => {
  switch (stepName) {
    case 'date':
      return 'communityJoinNotifications'
    case 'push':
      return steps.includes('email') ? 'communityJoinMail' : 'communityJoinCongrats'
    default:
      return 'communityJoinCongrats'
  }
}

type TUseCommunityJoinStepsParams = {
  stepName: TJoinStep
} & TIdSlugParam &
  TSearchParamsParam

export const useCommunityJoinSteps = ({ stepName, idSlug, searchParams }: TUseCommunityJoinStepsParams) => {
  const router = useRouter()
  const { routes } = useRoutes()

  const [id, slug] = idSlug ? parseIdDash(idSlug) : []
  const { course, origin, source } = filterSearchParams<TCommunityJoinParams>(searchParams)

  const { data: community, isPending: isCommunityPending } = useCommunityQuery(id ?? 0, { enabled: !!id })
  const { data: me, isPending: isMePending } = useMeQuery()

  const joinSteps = buildJoinSteps(community, me)

  const isPending = isCommunityPending || isMePending

  const stepIndex = joinSteps.indexOf(stepName ?? '')
  const stepperOptions = stepIndex >= 0 ? { currentStep: stepIndex + 1, totalSteps: joinSteps.length } : undefined

  const shouldBeDisplayed = isPending || joinSteps.includes(stepName ?? '')

  const goToNextStep = useCallback(() => {
    if (!id || !slug) return

    const nextRouteName = getNextJoinRouteName(stepName, joinSteps)

    router.navigate(routes[nextRouteName]({ id, slug, course, origin, source }))
  }, [id, slug, stepName, joinSteps, router, routes, course, origin, source])

  useEffect(() => {
    if (!shouldBeDisplayed && !isPending) goToNextStep()
  }, [shouldBeDisplayed, isPending, goToNextStep])

  return { goToNextStep, stepperOptions, isPending, me, community, shouldBeDisplayed }
}
